/// <reference path="../select_documents/SelectDocumentCell.ts" />

interface LinkEntityCandidate
{
    entityName:string;
    entityID:string;
    title:string;
    amount:number;
    date:Date;
}

interface SelectLinkEntityDelegate
{
    linkCandidatesDidSelected(controller:SelectLinkEntityViewController, candidates:LinkEntityCandidate[]):void;
}

class SuggestedLinkCell extends UITableViewCell
{
    delegate:SelectLinkEntityViewController = null;

    private dateLabel:MUILabel = null;
    private titleLabel:MUILabel = null;
    private detailLabel:MUILabel = null;
    private amountLabel:MUILabel = null;
    private addButton:MUIButton = null;

    awakeFromHTML(){
        this.dateLabel = MUIOutlet(this, "date-lbl", "MUILabel");
        this.titleLabel = MUIOutlet(this, "title-lbl", "MUILabel");
        this.detailLabel = MUIOutlet(this, "detail-lbl", "MUILabel");
        this.amountLabel = MUIOutlet(this, "amount-lbl", "MUILabel");
        this.addButton = MUIOutlet(this, "add-btn", "MUIButton");
        this.addButton.setAction(this, function(this:SuggestedLinkCell){
            if (this.delegate != null) this.delegate.suggestionCellDidTapAdd(this, this._suggestion);
        });

        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    private _suggestion:any = null;
    setSuggestion(suggestion:any, selected:boolean){
        this._suggestion = suggestion;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let dateString = suggestion["date"] as string;
        let date = dateString?.length > 0 ? ad.serverDateTimeFormatter.dateFromString(dateString.replace(" ", "T")) : null;
        this.dateLabel.text = date != null ? ad.dateTimeFormatter.stringFromDate(date) : (dateString?.length >= 10 ? dateString.substring(0, 10) : dateString);
        this.titleLabel.text = suggestion["title"];
        this.detailLabel.text = SelectLinkEntityViewController.displayNameForEntity(suggestion["entityName"]) + (suggestion["detail"]?.length > 0 ? " · " + suggestion["detail"] : "");
        this.amountLabel.text = ad.currencyFormatter.stringFromNumber(suggestion["amount"]);

        // The button stays visible and flips to a remove icon so the row can be deselected
        if (selected) {
            MUICoreLayerRemoveStyle(this.addButton.layer, "add");
            MUICoreLayerAddStyle(this.addButton.layer, "remove");
        }
        else {
            MUICoreLayerRemoveStyle(this.addButton.layer, "remove");
            MUICoreLayerAddStyle(this.addButton.layer, "add");
        }

        let labels = [this.dateLabel, this.titleLabel, this.detailLabel, this.amountLabel];
        for (let index = 0; index < labels.length; index++){
            if (selected) MUICoreLayerAddStyle(labels[index].layer, "light");
            else MUICoreLayerRemoveStyle(labels[index].layer, "light");
        }
    }
}

// Same as SelectDocumentCell but selected rows keep the button visible as a
// remove icon, so a selection can be undone (this picker has no side list)
class LinkDocumentCell extends SelectDocumentCell
{
    set selectedItem(value:boolean){
        let cell = this as any;
        let button = cell.addButton as MUIButton;
        button.hidden = false;
        if (value) {
            MUICoreLayerRemoveStyle(button.layer, "add");
            MUICoreLayerAddStyle(button.layer, "remove");
        }
        else {
            MUICoreLayerRemoveStyle(button.layer, "remove");
            MUICoreLayerAddStyle(button.layer, "add");
        }

        let labels = [cell.dateLabel, cell.documentIDLabel, cell.legalNameLabel, cell.totalLabel];
        for (let index = 0; index < labels.length; index++){
            if (labels[index] == null) continue;
            if (value) MUICoreLayerAddStyle(labels[index].layer, "light");
            else MUICoreLayerRemoveStyle(labels[index].layer, "light");
        }
    }
}

class SelectLinkEntityViewController extends MUIViewController
{
    static newInstance(): SelectLinkEntityViewController {
        let vc = new SelectLinkEntityViewController("select-link-entity-view");
        vc.initWithResource("layout/bankaccount/SelectLinkEntityView.html");
        return vc;
    }

    delegate:SelectLinkEntityDelegate = null;
    movement:BankMovement = null;

    get preferredContentSize(){ return new MIOSize(900, 700); }

    static displayNameForEntity(entityName:string) : string {
        switch (entityName) {
            case "PurchaseInvoice": return MIOLocalizeString("PURCHASES", "PURCHASES");
            case "SalesInvoice": return MIOLocalizeString("SALES", "SALES");
            case "LicenseInvoice": return MIOLocalizeString("SALES", "SALES");
            case "ArchivedDocument": return MIOLocalizeString("POS", "POS");
            case "ArchivedTicket": return MIOLocalizeString("POS", "POS");
            case "ArchivedInvoice": return MIOLocalizeString("POS", "POS");
            case "EmployeePayroll": return MIOLocalizeString("PAYROLLS", "PAYROLLS");
            case "Loan": return MIOLocalizeString("LOANS", "LOANS");
            case "BankMovement": return MIOLocalizeString("BANK", "BANK");
        }
        return entityName;
    }

    private closeButton:MUIButton = null;
    private linkButton:MUIButton = null;
    private segmentedControl:MUISegmentedControl = null;
    private filterBar:MUIView = null;
    private totalLabel:MUILabel = null;
    private movementAmountLabel:MUILabel = null;
    private movementDateLabel:MUILabel = null;
    private movementConceptLabel:MUILabel = null;
    private movementHeaderAmountLabel:MUILabel = null;
    private loadingSpinner:MUIView = null;

    private bankFilterBar:MUIView = null;
    private bankAccountDropdown:MUIButton = null;
    private beginDateTextField:MUITextField = null;
    private endDateTextField:MUITextField = null;
    private calendarButton:MUIButton = null;

    private dateFilterTextField:ColumnFilterTextField = null;
    private documentIDFilterTextField:ColumnFilterTextField = null;
    private legalNameFilterTextField:ColumnFilterTextField = null;
    private totalFilterTextField:ColumnFilterTextField = null;

    private tableView:UITableView = null;
    private columnFilterController:ColumnFilterController = null;

    viewDidLoad(){
        super.viewDidLoad();

        this.columnFilterController = new ColumnFilterController();
        this.columnFilterController.initWithDelegate(this);

        this.closeButton = MUIOutlet(this, "close-btn", "MUIButton");
        this.closeButton.setAction(this, function(this:SelectLinkEntityViewController){
            this.dismissViewController(true);
        });

        this.linkButton = MUIOutlet(this, "link-btn", "MUIButton");
        this.linkButton.setAction(this, function(this:SelectLinkEntityViewController){
            if (this.selectedCandidates.length == 0) return;
            this.dismissViewController(true);
            if (this.delegate != null) this.delegate.linkCandidatesDidSelected(this, this.selectedCandidates);
        });

        this.segmentedControl = MUIOutlet(this, "segmented-control", "MUISegmentedControl");
        this.segmentedControl.setAction(this, function(this:SelectLinkEntityViewController, control:MUISegmentedControl, index:number){
            this.configureSegment(index);
        });

        this.filterBar = MUIOutlet(this, "filter-bar", "MUIView");
        this.totalLabel = MUIOutlet(this, "selected-total-lbl", "MUILabel");
        this.movementAmountLabel = MUIOutlet(this, "movement-amount-lbl", "MUILabel");
        this.movementDateLabel = MUIOutlet(this, "movement-date-lbl", "MUILabel");
        this.movementConceptLabel = MUIOutlet(this, "movement-concept-lbl", "MUILabel");
        this.movementHeaderAmountLabel = MUIOutlet(this, "movement-header-amount-lbl", "MUILabel");

        this.loadingSpinner = MUIOutlet(this, "loading-spinner", "MUIView");
        this.loadingSpinner.hidden = true;

        this.bankFilterBar = MUIOutlet(this, "bank-filter-bar", "MUIView");
        this.bankFilterBar.hidden = true;

        this.bankAccountDropdown = MUIOutlet(this, "bank-account-dd", "MUIButton");
        this.bankAccountDropdown.setAction(this, this.showBankAccountFilterOptions);

        this.beginDateTextField = MUIOutlet(this, "begin-date-tf", "MUITextField");
        this.beginDateTextField.setOnDidEndEditing(this, function(this:SelectLinkEntityViewController, control:MUITextField){
            let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
            let date = ad.dateFormatter.dateFromString(control.text) ?? ad.dateTimeFormatter.dateFromString(control.text);
            if (date != null) this.filterBeginDate = date;
            this.updateBankFilterDates();
            this.reloadBankTab();
        });

        this.endDateTextField = MUIOutlet(this, "end-date-tf", "MUITextField");
        this.endDateTextField.setOnDidEndEditing(this, function(this:SelectLinkEntityViewController, control:MUITextField){
            let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
            let date = ad.dateFormatter.dateFromString(control.text) ?? ad.dateTimeFormatter.dateFromString(control.text);
            if (date != null) this.filterEndDate = date;
            this.updateBankFilterDates();
            this.reloadBankTab();
        });

        this.calendarButton = MUIOutlet(this, "calendar-btn", "MUIButton");
        this.calendarButton.setAction(this, function(this:SelectLinkEntityViewController){
            AppHelper.sharedInstance().presentDateSelectionViewController(null, this);
        });

        this.dateFilterTextField = MUIOutlet(this, "date-filter-tf", "ColumnFilterTextField");
        this.dateFilterTextField.filterController = this.columnFilterController;
        this.dateFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.DateTime, "date", null, null);

        this.documentIDFilterTextField = MUIOutlet(this, "document-filter-tf", "ColumnFilterTextField");
        this.documentIDFilterTextField.filterController = this.columnFilterController;
        this.documentIDFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "documentNumber", null, null);

        this.legalNameFilterTextField = MUIOutlet(this, "client-filter-tf", "ColumnFilterTextField");
        this.legalNameFilterTextField.filterController = this.columnFilterController;
        this.legalNameFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "legalEntity.name", null, null);

        this.totalFilterTextField = MUIOutlet(this, "total-filter-tf", "ColumnFilterTextField");
        this.totalFilterTextField.filterController = this.columnFilterController;
        this.totalFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.Number, "totalAmount", null, null);

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;
    }

    viewWillAppear(animated?:boolean){
        super.viewWillAppear(animated);

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        this.movementAmountLabel.text = ad.currencyFormatter.stringFromNumber(this.movement?.amount ?? 0);
        this.movementDateLabel.text = this.movement != null ? ad.dateFormatter.stringFromDate(this.movement.date) : null;
        this.movementConceptLabel.text = this.movement?.concept;
        this.movementHeaderAmountLabel.text = ad.currencyFormatter.stringFromNumber(this.movement?.amount ?? 0);

        this.selectedCandidates = [];
        this.updateTotalLabel();

        // Bank tab defaults: any account, one week around the movement date
        this.filterAccount = null;
        this.bankAccountDropdown.title = MIOLocalizeString("ALL", "ALL");
        let movementDate = this.movement?.date ?? new Date();
        this.filterBeginDate = new Date(movementDate.getTime() - 7 * 86400000);
        this.filterEndDate = new Date(movementDate.getTime() + 7 * 86400000);
        this.updateBankFilterDates();

        this.segmentedControl.selectSegmentedAtIndex(0);
        this.configureSegment(0);
        this.loadSuggestions();
    }

    //
    // Bank tab filters
    //

    private filterAccount:BankAccount = null;
    private filterBeginDate:Date = null;
    private filterEndDate:Date = null;

    private updateBankFilterDates(){
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        this.beginDateTextField.text = this.filterBeginDate != null ? ad.dateFormatter.stringFromDate(this.filterBeginDate) : null;
        this.endDateTextField.text = this.filterEndDate != null ? ad.dateFormatter.stringFromDate(this.filterEndDate) : null;
    }

    private reloadBankTab(){
        if (this.currentSegment != 6) return;
        this.configureSegment(6);
    }

    datesDidSelect(dateTimeFrom:Date, dateTimeTo:Date){
        this.filterBeginDate = dateTimeFrom;
        this.filterEndDate = dateTimeTo;
        this.updateBankFilterDates();
        this.reloadBankTab();
    }

    private showBankAccountFilterOptions(){
        DBHelper.queryObjectsWithCompletion("BankAccount", null, MIOPredicate.predicateWithFormat("deletedAt = null"), ["info", "bank"], this, function(this:SelectLinkEntityViewController, objects:BankAccount[]){
            let avc = new MUIAlertViewController();
            avc.initWithTitle(MIOLocalizeString("ACCOUNT", "ACCOUNT"), MIOLocalizeString("SELECT_AN_ACCOUNT", "SELECT AN ACCOUNT"), MUIAlertViewStyle.Default);

            avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("ALL", "ALL"), MUIAlertActionStyle.Default, this, function(this:SelectLinkEntityViewController){
                this.filterAccount = null;
                this.bankAccountDropdown.title = MIOLocalizeString("ALL", "ALL");
                this.reloadBankTab();
            }));

            for (let index = 0; index < objects.length; index++){
                let account = objects[index];
                if (account.identifier == this.movement?.bankAccount?.identifier) continue; // the movement account is not a transfer target
                let title = (account.bank?.name ?? "") + " " + (account.info?.number ?? "");
                avc.addAction(MUIAlertAction.alertActionWithTitle(title.trim(), MUIAlertActionStyle.Default, this, function(this:SelectLinkEntityViewController){
                    this.filterAccount = account;
                    this.bankAccountDropdown.title = title.trim();
                    this.reloadBankTab();
                }));
            }

            avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));
            this.presentViewController(avc, true);
        });
    }

    //
    // Suggestions (server side)
    //

    private suggestions:any[] = [];
    private loadSuggestions(){
        if (this.movement == null) return;

        this.suggestions = [];
        this.loadingSpinner.hidden = false;
        if (this.currentSegment == 0) this.tableView.reloadData();

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.webService.bankMovementLinkSuggestions(this.movement, (inferredEntity:string, suggestions:any[], error:string) => {
            this.loadingSpinner.hidden = true;
            this.suggestions = error != null ? [] : suggestions;
            if (this.currentSegment == 0) this.tableView.reloadData();
        });
    }

    //
    // Segments
    //

    private currentSegment = 0;
    private entityName = "PurchaseInvoice";
    private documentIDKey = "documentNumber";
    private totalAmountKey = "totalAmount";
    private sortDateKey = "date";
    private predicateFormat:string = null;
    private relations:string[] = [];

    private configureSegment(index:number){
        this.currentSegment = index;
        this.filterBar.hidden = index == 0;
        this.bankFilterBar.hidden = index != 6;
        this.relations = [];
        this.sortDateKey = "date";
        this.predicateFormat = null;

        switch (index) {
            case 1: // Purchase invoices
                this.entityName = "PurchaseInvoice";
                this.documentIDKey = "documentNumber";
                this.totalAmountKey = "totalAmount";
                this.relations = ["legalEntity"];
                this.documentIDFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "documentNumber", null, null);
                this.legalNameFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "legalEntity.name", null, null);
                this.totalFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.Number, "totalAmount", null, null);
                break;

            case 2: // Sales invoices (includes license invoices)
                this.entityName = "SalesInvoice";
                this.documentIDKey = "documentNumber";
                this.totalAmountKey = "totalAmount";
                this.relations = ["legalEntity"];
                this.documentIDFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "documentNumber", null, null);
                this.legalNameFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "legalEntity.name", null, null);
                this.totalFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.Number, "totalAmount", null, null);
                break;

            case 3: // POS documents (archived tickets and invoices)
                this.entityName = "ArchivedDocument";
                this.documentIDKey = "legalDocumentID";
                this.totalAmountKey = "totalPrice";
                this.predicateFormat = "legalDocumentID != null AND nextDocument == null AND modifiedType != 1 AND totalPrice != 0";
                this.documentIDFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "legalDocumentID", null, null);
                this.legalNameFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "linkedLegalEntityName", null, null);
                this.totalFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.Number, "totalPrice", null, null);
                break;

            case 4: // Salary payrolls
                this.entityName = "EmployeePayroll";
                this.documentIDKey = "period";
                this.totalAmountKey = "grossAmount";
                this.sortDateKey = "beginDate";
                this.relations = ["worker"];
                this.documentIDFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "period", null, null);
                this.legalNameFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "worker.name", null, null);
                this.totalFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.Number, "grossAmount", null, null);
                break;

            case 5: // Loans
                this.entityName = "Loan";
                this.documentIDKey = "name";
                this.totalAmountKey = "capitalAmount";
                this.sortDateKey = "startDate";
                this.documentIDFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "name", null, null);
                this.legalNameFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "reference", null, null);
                this.totalFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.Number, "capitalAmount", null, null);
                break;

            case 6: // Bank movements from other accounts (transfers between own accounts)
                this.entityName = "BankMovement";
                this.documentIDKey = "concept";
                this.totalAmountKey = "amount";
                this.relations = ["bankAccount"];

                let parts = [];
                if (this.filterAccount != null) {
                    parts.push("bankAccount.identifier == " + this.filterAccount.identifier);
                }
                else if (this.movement != null && this.movement.bankAccount != null) {
                    // A transfer goes to another account
                    parts.push("bankAccount.identifier != " + this.movement.bankAccount.identifier);
                }
                if (this.filterBeginDate != null) parts.push("date >= '" + MIODateGetDateString(this.filterBeginDate) + " 00:00:00'");
                if (this.filterEndDate != null) parts.push("date <= '" + MIODateGetDateString(this.filterEndDate) + " 23:59:59'");
                this.predicateFormat = parts.length > 0 ? parts.join(" AND ") : null;

                this.documentIDFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "concept", null, null);
                this.legalNameFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "beneficiaryName", null, null);
                this.totalFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.Number, "amount", null, null);
                break;
        }

        this.fetchedResultsController = null;
        this.tableView.reloadData();
    }

    //
    // Table view
    //

    numberOfSections(tableView:UITableView){
        if (this.currentSegment == 0) return 1;
        return this.fetchedResultsController.sections.length;
    }

    numberOfRowsInSection(tableView:UITableView, section:number){
        if (this.currentSegment == 0) return this.suggestions.length;
        let sec = this.fetchedResultsController.sections[section];
        return sec.numberOfObjects();
    }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        if (this.currentSegment == 0) {
            let cell = tableView.dequeueReusableCellWithIdentifier("SuggestionCell") as SuggestedLinkCell;
            let suggestion = this.suggestions[indexPath.row];
            cell.setSuggestion(suggestion, this.isSelected(suggestion["entityID"]));
            cell.delegate = this;
            return cell;
        }

        let cell = tableView.dequeueReusableCellWithIdentifier("DocumentCell") as LinkDocumentCell;
        let item = this.fetchedResultsController.objectAtIndexPath(indexPath) as MIOManagedObject;
        cell.item = item;
        cell.delegate = this;
        cell.selectedItem = this.isSelected(item.valueForKey("identifier"));
        return cell;
    }

    //
    // Selection (mixed: suggestions from the server, managed objects from the manual tabs)
    //

    private selectedCandidates:LinkEntityCandidate[] = [];

    private isSelected(entityID:string) : boolean {
        for (let index = 0; index < this.selectedCandidates.length; index++){
            if (this.selectedCandidates[index].entityID == entityID) return true;
        }
        return false;
    }

    private toggleCandidate(candidate:LinkEntityCandidate){
        for (let index = 0; index < this.selectedCandidates.length; index++){
            if (this.selectedCandidates[index].entityID == candidate.entityID) {
                this.selectedCandidates.splice(index, 1);
                this.updateTotalLabel();
                this.tableView.reloadData();
                return;
            }
        }
        this.selectedCandidates.push(candidate);
        this.updateTotalLabel();
        this.tableView.reloadData();
    }

    suggestionCellDidTapAdd(cell:SuggestedLinkCell, suggestion:any){
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        let dateString = suggestion["date"] as string;
        let date = dateString?.length > 0 ? ad.serverDateTimeFormatter.dateFromString(dateString.replace(" ", "T")) : null;

        // The counterpart movement of a transfer is identified by its date
        let title = suggestion["title"] as string;
        if (suggestion["entityName"] == "BankMovement" && date != null) {
            title = ad.dateFormatter.stringFromDate(date) + " · " + title;
        }

        this.toggleCandidate({
            entityName: suggestion["entityName"],
            entityID: suggestion["entityID"],
            title: title,
            amount: suggestion["amount"],
            date: date
        });
    }

    // Called by SelectDocumentCell
    addDocumentFromCell(cell:SelectDocumentCell, item:MIOManagedObject){
        let title = SelectLinkEntityViewController.titleForObject(item);
        if (item instanceof BankMovement) {
            let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
            title = ad.dateFormatter.stringFromDate((item as BankMovement).date) + " · " + title;
        }

        this.toggleCandidate({
            entityName: (item.constructor as any).name,
            entityID: item.valueForKey("identifier"),
            title: title,
            amount: SelectLinkEntityViewController.amountForObject(item),
            date: SelectLinkEntityViewController.dateForObject(item)
        });
    }

    static titleForObject(doc:MIOManagedObject) : string {
        if (doc instanceof Invoice) return doc.documentNumber;
        if (doc instanceof ArchivedDocument) return doc.legalDocumentID ?? doc.documentID;
        if (doc instanceof EmployeePayroll) return ((doc.worker?.name ?? "") + (doc.period != null ? " - " + doc.period : "")).trim();
        if (doc instanceof Loan) return doc.name;
        if (doc instanceof BankMovement) return doc.concept;
        return (doc.constructor as any).name;
    }

    static amountForObject(doc:MIOManagedObject) : number {
        if (doc instanceof Invoice) return doc.totalAmount;
        if (doc instanceof ArchivedDocument) return doc.totalPrice;
        if (doc instanceof EmployeePayroll) return doc.grossAmount;
        if (doc instanceof Loan) return doc.capitalAmount;
        if (doc instanceof BankMovement) return doc.amount;
        return null;
    }

    static dateForObject(doc:MIOManagedObject) : Date {
        if (doc instanceof Invoice) return doc.date;
        if (doc instanceof ArchivedDocument) return doc.date;
        if (doc instanceof EmployeePayroll) return doc.endDate ?? doc.beginDate;
        if (doc instanceof Loan) return doc.startDate;
        if (doc instanceof BankMovement) return doc.date;
        return null;
    }

    private updateTotalLabel(){
        let total = 0;
        for (let index = 0; index < this.selectedCandidates.length; index++) total += this.selectedCandidates[index].amount ?? 0;
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        this.totalLabel.text = ad.currencyFormatter.stringFromNumber(total);

        // Green when the selection balances the movement, red while it does not
        let matches = this.selectedCandidates.length > 0 && Math.abs(Math.abs(total) - Math.abs(this.movement?.amount ?? 0)) <= 0.005;
        if (matches) {
            MUICoreLayerRemoveStyle(this.totalLabel.layer, "alert");
            MUICoreLayerAddStyle(this.totalLabel.layer, "green");
        }
        else {
            MUICoreLayerRemoveStyle(this.totalLabel.layer, "green");
            if (this.selectedCandidates.length > 0) MUICoreLayerAddStyle(this.totalLabel.layer, "alert");
            else MUICoreLayerRemoveStyle(this.totalLabel.layer, "alert");
        }
    }

    //
    // Fetch (manual tabs)
    //

    private _fetchedResultsController:MWSFetchedResultsController = null;
    set fetchedResultsController(value){
        if (value == null && this._fetchedResultsController != null)
            this._fetchedResultsController.delegate = null;

        this._fetchedResultsController = value;
    }

    get fetchedResultsController(){
        if (this._fetchedResultsController != null) return this._fetchedResultsController;

        let ad = MUIWebApplication.sharedInstance().delegate;

        let filterFormat = this.columnFilterController.filterPredicateFormat();
        let predicates = [];
        if (this.predicateFormat != null) predicates.push(this.predicateFormat);
        if (filterFormat != null) predicates.push(filterFormat);

        let sortDescriptors = [ MIOSortDescriptor.sortDescriptorWithKey(this.sortDateKey, false),
                                MIOSortDescriptor.sortDescriptorWithKey(this.documentIDKey, false) ];

        let fetchRequest = DBHelper.listFetchRequestWithEntityName(this.entityName, sortDescriptors, predicates.length > 0 ? predicates.join(" AND ") : null);
        fetchRequest.relationshipKeyPathsForPrefetching = this.relations;
        fetchRequest.fetchLimit = 100;

        let fetchedResultsController = new MWSFetchedResultsController();
        fetchedResultsController.initWithFetchRequest(fetchRequest, ad.managedObjectContext, null);
        fetchedResultsController.webPersistentStore = ad.webPersistentStore;
        fetchedResultsController.delegate = this;

        fetchedResultsController.performFetch();

        this._fetchedResultsController = fetchedResultsController;
        return this._fetchedResultsController;
    }

    controllerDidChangeContent(controller:MIOFetchedResultsController){
        this.tableView.reloadData();
    }

    filterPredicateDidChange(controller:ColumnFilterController){
        this.fetchedResultsController = null;
        this.tableView.reloadData();
    }
}
